import { useState, useRef, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { Camera, Upload, X, Loader2, DollarSign } from "lucide-react";

interface MenuItem {
  id: string;
  name: string;
  description?: string;
  price: number;
  image?: string;         // Thumbnail (300px) for lists
  headerImage?: string;   // Header (800px) for detail modal
  available: boolean;
  menuId: number;
  categoryId: number;
}

interface MenuItemEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  item: MenuItem | null;
  restaurantId: string;
  restaurantProfile: string;
  menuId?: number;
}

export default function MenuItemEditModal({
  isOpen,
  onClose,
  item,
  restaurantId,
  restaurantProfile,
  menuId,
}: MenuItemEditModalProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [, navigate] = useLocation();
  
  const handleTokenExpired = () => {
    sessionStorage.removeItem('partnerEatzyToken');
    localStorage.removeItem('partnerRestaurantId');
    localStorage.removeItem('partnerRestaurantName');
    toast({
      title: "Session Expired",
      description: "Your session has expired. Please log in again.",
      variant: "destructive",
    });
    navigate('/partner/login');
  };
  
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [price, setPrice] = useState("");
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isLoadingItem, setIsLoadingItem] = useState(false);

  // Fetch full item details when modal opens to get image URLs
  useEffect(() => {
    if (isOpen && item && restaurantId) {
      const effectiveMenuId = item.menuId ?? menuId;
      if (!effectiveMenuId) {
        // No menuId, just use the item data we have
        setName(item.name);
        setDescription(item.description || "");
        setPrice(item.price.toFixed(2));
        setImagePreview(item.headerImage || item.image || null);
        setImageFile(null);
        return;
      }

      // Fetch full item details to get media/image URLs
      const fetchItemDetails = async () => {
        setIsLoadingItem(true);
        try {
          const authToken = localStorage.getItem("auth_token");
          const eatzyToken = sessionStorage.getItem("partnerEatzyToken");
          
          const response = await fetch(
            `/api/partner/restmanager/${restaurantId}/menus/${effectiveMenuId}/items/${item.id}`,
            {
              method: "GET",
              headers: {
                "Content-Type": "application/json",
                ...(authToken && { Authorization: `Bearer ${authToken}` }),
                ...(eatzyToken && { "X-Eatzy-Token": eatzyToken }),
              },
            }
          );

          if (response.ok) {
            const fullItemData = await response.json();
            console.log("[MenuItemEditModal] Fetched full item data:", fullItemData);
            
            // Update form with full data
            setName(fullItemData.name || item.name);
            setDescription(fullItemData.description || item.description || "");
            setPrice((fullItemData.price ?? item.price).toFixed(2));
            // Use headerImage for preview, fallback to thumbnail
            setImagePreview(fullItemData.headerImage || fullItemData.image || item.headerImage || item.image || null);
          } else {
            // Check for token expiration
            const errorData = await response.json().catch(() => ({}));
            if (response.status === 401 || errorData.code === "TOKEN_EXPIRED") {
              handleTokenExpired();
              return;
            }
            
            // If fetch fails for other reasons, fallback to item data we have
            console.warn("[MenuItemEditModal] Failed to fetch item details, using cached data");
            setName(item.name);
            setDescription(item.description || "");
            setPrice(item.price.toFixed(2));
            setImagePreview(item.headerImage || item.image || null);
          }
        } catch (error) {
          console.error("[MenuItemEditModal] Error fetching item details:", error);
          // Fallback to item data we have
          setName(item.name);
          setDescription(item.description || "");
          setPrice(item.price.toFixed(2));
          setImagePreview(item.headerImage || item.image || null);
        } finally {
          setIsLoadingItem(false);
          setImageFile(null);
        }
      };

      fetchItemDetails();
    }
  }, [isOpen, item, restaurantId, menuId]);

  // Update mutation - uses Eatzy API POST /restmanager/{restaurantId}/menus/{menuId}/items/{itemId}
  const updateMutation = useMutation({
    mutationFn: async (data: {
      name: string;
      description: string;
      price: number;
      categoryId?: number;
    }) => {
      const authToken = localStorage.getItem("auth_token");
      const eatzyToken = sessionStorage.getItem("partnerEatzyToken");
      
      // Get the menuId from the item or prop (use nullish coalescing to handle 0 as valid)
      const effectiveMenuId = item?.menuId ?? menuId;
      if (effectiveMenuId === undefined || effectiveMenuId === null) {
        throw new Error("Menu ID is required to update item");
      }
      
      const response = await fetch(
        `/api/partner/restmanager/${restaurantId}/menus/${effectiveMenuId}/items/${item?.id}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: authToken ? `Bearer ${authToken}` : "",
            "X-Eatzy-Token": eatzyToken || "",
          },
          body: JSON.stringify({
            ...data,
            categoryId: item?.categoryId,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        if (errorData.code === 'TOKEN_EXPIRED' || response.status === 401) {
          throw new Error('TOKEN_EXPIRED');
        }
        throw new Error(errorData.message || "Failed to update menu item");
      }

      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Item Updated",
        description: "Menu item has been updated successfully.",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/partner/menu", restaurantProfile] });
      onClose();
    },
    onError: (error: Error) => {
      if (error.message === 'TOKEN_EXPIRED' || error.message?.includes('Session expired')) {
        handleTokenExpired();
        return;
      }
      toast({
        title: "Update Failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Image upload handler
  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith("image/")) {
        toast({
          title: "Invalid File",
          description: "Please select an image file.",
          variant: "destructive",
        });
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast({
          title: "File Too Large",
          description: "Image must be less than 5MB.",
          variant: "destructive",
        });
        return;
      }

      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setImagePreview(null);
    setImageFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSave = async () => {
    // Validate inputs
    const parsedPrice = parseFloat(price);
    if (!name.trim()) {
      toast({
        title: "Validation Error",
        description: "Item name is required.",
        variant: "destructive",
      });
      return;
    }
    if (isNaN(parsedPrice) || parsedPrice < 0) {
      toast({
        title: "Validation Error",
        description: "Please enter a valid price.",
        variant: "destructive",
      });
      return;
    }

    // Upload image first if a new one was selected
    // Note: Image upload is handled separately via Eatzy's media upload API
    // The uploaded image will be associated with the item via the media array
    if (imageFile) {
      setIsUploading(true);
      try {
        const formData = new FormData();
        formData.append("image", imageFile);
        
        const authToken = localStorage.getItem("auth_token");
        const eatzyToken = sessionStorage.getItem("partnerEatzyToken");
        
        // Get the menuId from the item
        const effectiveMenuId = item?.menuId;
        if (!effectiveMenuId) {
          throw new Error("Menu ID is required to upload image");
        }
        
        const uploadResponse = await fetch(
          `/api/partner/restmanager/${restaurantId}/menus/${effectiveMenuId}/items/${item?.id}/image`,
          {
            method: "POST",
            headers: {
              Authorization: authToken ? `Bearer ${authToken}` : "",
              "X-Eatzy-Token": eatzyToken || "",
            },
            body: formData,
          }
        );

        if (!uploadResponse.ok) {
          const errorData = await uploadResponse.json().catch(() => ({}));
          if (errorData.code === 'TOKEN_EXPIRED' || uploadResponse.status === 401) {
            handleTokenExpired();
            return;
          }
          throw new Error(errorData.message || "Failed to upload image");
        }

        // Image upload successful - the backend will update the item's media array
        await uploadResponse.json();
      } catch (error: any) {
        if (error.message?.includes('Session expired')) {
          handleTokenExpired();
          return;
        }
        toast({
          title: "Image Upload Failed",
          description: error.message || "Could not upload the image. Item will be saved without the new image.",
          variant: "destructive",
        });
      } finally {
        setIsUploading(false);
      }
    }

    // Save the item (name, description, price only - images handled separately)
    updateMutation.mutate({
      name: name.trim(),
      description: description.trim(),
      price: parsedPrice,
    });
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onClose();
    }
    // Form is initialized via useEffect when modal opens
  };

  if (!item) return null;

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit Menu Item</DialogTitle>
        </DialogHeader>

        {isLoadingItem ? (
          <div className="flex flex-col items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground mb-2" />
            <p className="text-sm text-muted-foreground">Loading item details...</p>
          </div>
        ) : (
        <div className="space-y-4 py-4">
          {/* Image Upload */}
          <div className="space-y-2">
            <Label>Item Image</Label>
            <div className="flex items-center gap-4">
              {imagePreview ? (
                <div className="relative w-24 h-24 rounded-lg overflow-hidden border">
                  <img
                    src={imagePreview}
                    alt="Item preview"
                    className="w-full h-full object-cover"
                  />
                  <button
                    type="button"
                    onClick={handleRemoveImage}
                    className="absolute top-1 right-1 p-1 bg-black/50 rounded-full text-white hover:bg-black/70"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ) : (
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className="w-24 h-24 border-2 border-dashed rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-primary hover:bg-muted/50 transition-colors"
                >
                  <Camera className="w-6 h-6 text-muted-foreground" />
                  <span className="text-xs text-muted-foreground mt-1">Add Photo</span>
                </div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageSelect}
                className="hidden"
              />
              {imagePreview && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Change
                </Button>
              )}
            </div>
          </div>

          {/* Item Name */}
          <div className="space-y-2">
            <Label htmlFor="item-name">Item Name</Label>
            <Input
              id="item-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter item name"
            />
          </div>

          {/* Price */}
          <div className="space-y-2">
            <Label htmlFor="item-price">Price</Label>
            <div className="relative">
              <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                id="item-price"
                type="number"
                step="0.01"
                min="0"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                className="pl-8"
                placeholder="0.00"
              />
            </div>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="item-description">Description</Label>
            <Textarea
              id="item-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter item description"
              rows={3}
            />
          </div>
        </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={updateMutation.isPending || isUploading}
          >
            {(updateMutation.isPending || isUploading) && (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            )}
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
